import React, { useMemo, useState } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DevicesOtherOutlined from '@mui/icons-material/DevicesOtherOutlined';
import MeetingRoomOutlined from '@mui/icons-material/MeetingRoomOutlined';
import { Card, Badge, Button, IconButton, EmptyState, PageLoader, Alert, Modal } from '../../../components/ui';
import PasAssetDialog from './PasAssetDialog';

/** Device status → semantic badge variant. */
const statusVariantFor = (status) =>
    ({
        Active: 'success',
        Maintenance: 'warning',
        Error: 'danger',
        Disconnected: 'danger',
        Inactive: 'neutral',
    })[status] || 'neutral';

/** Key/value chip used for device facts. */
const Fact = ({ label, value }) => (
    <span className="inline-flex items-center gap-1 rounded-sm border border-border bg-muted px-2 py-1 text-xs">
        <span className="font-medium text-muted-fg">{label}</span>
        <span className="text-foreground">{value}</span>
    </span>
);

const getLocationLabel = (details = {}) =>
    details.stationLocation ||
    details.speakerLocation ||
    details.loopLocation ||
    details.arrayLocation ||
    details.iedLocation ||
    details.noiseSensorLocation ||
    details.sensorLocation ||
    details.audioBridgeLocation ||
    details.bridgeLocation ||
    details.location ||
    details.relatedSCRRoom ||
    null;

const formatZoneGroup = (zoneGroup) => {
    if (!zoneGroup) return null;
    if (typeof zoneGroup === 'string') return zoneGroup;
    if (typeof zoneGroup === 'object' && (zoneGroup.name || zoneGroup.zoneID)) {
        return zoneGroup.name || zoneGroup.zoneID;
    }
    return null;
};

const DeviceList = ({ controller, devices = [], deviceType, onBack, onDevicesChanged, loading, errorMessage }) => {
    const [selectedScr, setSelectedScr] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [addingAsset, setAddingAsset] = useState(false);
    const controllerName = controller?.description || controller?.name || 'Controller';
    const isMainframeView = deviceType === 'Mainframe';

    const scrGroups = useMemo(() => {
        if (!isMainframeView) return [];
        const map = {};

        devices.forEach((device) => {
            const details = device.deviceDetails || {};
            const scrRaw =
                details.SCRNumber ||
                details.scr ||
                details.SCR ||
                details.scrNumber ||
                details.scrRoom ||
                '';
            const scr = scrRaw ? scrRaw.toString().trim() : 'Unknown SCR';

            if (!map[scr]) {
                map[scr] = { scr, devices: [] };
            }
            map[scr].devices.push(device);
        });

        return Object.values(map).sort((a, b) =>
            a.scr.toLowerCase().localeCompare(b.scr.toLowerCase(), undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [devices, isMainframeView]);

    const cards = useMemo(() => {
        // Sort mainframes by SCR room (SCRNumber) before building cards; otherwise keep original order
        const sortedDevices = [...devices].sort((a, b) => {
            if (deviceType !== 'Mainframe') return 0;
            const aScr = (a?.deviceDetails?.SCRNumber || '').toString().toLowerCase();
            const bScr = (b?.deviceDetails?.SCRNumber || '').toString().toLowerCase();
            if (aScr && bScr) return aScr.localeCompare(bScr, undefined, { numeric: true, sensitivity: 'base' });
            if (aScr) return -1;
            if (bScr) return 1;
            return 0;
        });

        return sortedDevices.map((device) => {
            const details = device.deviceDetails || {};
            const location = device.assetLocation || getLocationLabel(details);
            const zone = details.zoneNumber || details.ACSGroup || formatZoneGroup(details.zoneGroup);
            const channel = details.channelNumber;
            const model = device.vendorPartNumber || details.model || details.iedModel;
            const ipAddress = device.ipAddress || details.ipAddress;

            const quickFacts = [
                { label: 'Project', value: device.project },
                { label: 'Building', value: device.building },
                { label: 'Location', value: location },
                { label: 'Floor', value: device.floor },
                { label: 'Category', value: device.inventoryCategory },
                { label: 'Zone', value: zone },
                { label: 'Channel', value: channel },
                { label: 'Model', value: model },
                { label: 'IP', value: ipAddress },
                { label: 'MAC', value: device.macAddress || details.macAddress },
                { label: 'Port / switch', value: device.portSwitchNumber },
            ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '');

            return {
                id: device._id || device.id,
                name: device.name,
                status: device.status || 'Active',
                quickFacts,
                device,
            };
        });
    }, [devices, deviceType]);

    return (
        <div className="animate-fade-in">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <IconButton icon={ArrowBack} label="Back to device types" onClick={onBack} variant="secondary" />
                    <div className="min-w-0">
                        <p className="truncate text-sm text-muted-fg">
                            {deviceType} in {controllerName}
                        </p>
                        <h2 className="text-lg font-semibold text-foreground sm:text-xl">
                            <span className="tabular">{devices.length}</span> {deviceType}{' '}
                            {devices.length === 1 ? 'device' : 'devices'}
                        </h2>
                    </div>
                </div>
                <Button icon={AddOutlined} onClick={() => setAddingAsset(true)}>Add asset</Button>
            </div>

            {loading && <PageLoader label="Loading devices…" />}

            {!loading && errorMessage && <Alert variant="error">{errorMessage}</Alert>}

            {!loading && !errorMessage && devices.length === 0 && (
                <EmptyState
                    icon={DevicesOtherOutlined}
                    title={`No ${deviceType} devices`}
                    description="This controller has none of this device type."
                />
            )}

            {!loading && !errorMessage && devices.length > 0 && (
                <>
                    {isMainframeView ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {scrGroups.map((group) => (
                                <Card
                                    key={group.scr}
                                    interactive
                                    onClick={() => setSelectedScr(group)}
                                    className="group"
                                >
                                    <div className="flex items-center gap-4 p-5">
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors duration-base group-hover:bg-accent group-hover:text-accent-fg">
                                            <MeetingRoomOutlined style={{ fontSize: 22 }} aria-hidden="true" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs uppercase tracking-wide text-muted-fg">SCR room</p>
                                            <h3 className="truncate font-semibold text-foreground">{group.scr}</h3>
                                        </div>
                                        <Badge variant="neutral" size="sm">
                                            {group.devices.length}
                                        </Badge>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {cards.map((card) => (
                                <Card key={card.id} interactive onClick={() => setSelectedDevice(card.device)}>
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <h3 className="truncate font-semibold text-foreground">{card.name}</h3>
                                                <p className="mt-0.5 truncate text-sm text-muted-fg">{controllerName}</p>
                                            </div>
                                            <Badge variant={statusVariantFor(card.status)} size="sm" dot>
                                                {card.status}
                                            </Badge>
                                        </div>

                                        {card.quickFacts.length > 0 && (
                                            <div className="mt-4 flex flex-wrap gap-2">
                                                {card.quickFacts.map((fact, idx) => (
                                                    <Fact key={idx} label={fact.label} value={fact.value} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}

            <Modal
                isOpen={Boolean(selectedScr)}
                onClose={() => setSelectedScr(null)}
                size="lg"
                title={selectedScr ? `SCR room ${selectedScr.scr}` : ''}
                subtitle={
                    selectedScr
                        ? `${selectedScr.devices.length} mainframe${selectedScr.devices.length === 1 ? '' : 's'}`
                        : undefined
                }
            >
                {selectedScr && (
                    <div className="grid gap-4 sm:grid-cols-2">
                        {[...selectedScr.devices]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }))
                            .map((device) => {
                                const details = device.deviceDetails || {};
                                const quickFacts = [
                                    { label: 'Project', value: device.project },
                                    { label: 'Building', value: device.building },
                                    { label: 'Location', value: device.assetLocation },
                                    { label: 'Floor', value: device.floor },
                                    { label: 'Category', value: device.inventoryCategory },
                                    { label: 'Model', value: device.vendorPartNumber || details.model },
                                    { label: 'IP', value: device.ipAddress || details.ipAddress },
                                    { label: 'MAC', value: device.macAddress || details.macAddress },
                                    { label: 'Port / switch', value: device.portSwitchNumber },
                                    { label: 'Loops', value: details.loops },
                                    { label: 'Channels', value: details.mainframeTotalChannelCount },
                                    { label: 'Cards', value: details.TotalAmplifierCardCount },
                                ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '');

                                return (
                                    <Card
                                        key={device._id || device.id}
                                        interactive
                                        onClick={() => {
                                            setSelectedScr(null);
                                            setSelectedDevice(device);
                                        }}
                                        className="bg-muted/40"
                                    >
                                        <div className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <h4 className="truncate font-semibold text-foreground">{device.name}</h4>
                                                    <p className="truncate text-sm text-muted-fg">{controllerName}</p>
                                                </div>
                                                <Badge variant={statusVariantFor(device.status)} size="sm" dot>
                                                    {device.status || 'Active'}
                                                </Badge>
                                            </div>

                                            {quickFacts.length > 0 && (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {quickFacts.map((fact, idx) => (
                                                        <Fact key={idx} label={fact.label} value={fact.value} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                    </div>
                )}
            </Modal>

            <PasAssetDialog
                isOpen={addingAsset}
                createMode
                deviceType={deviceType}
                controller={controller}
                onClose={() => setAddingAsset(false)}
                onChanged={onDevicesChanged}
            />

            <PasAssetDialog
                isOpen={Boolean(selectedDevice)}
                device={selectedDevice}
                deviceType={deviceType}
                controller={controller}
                onClose={() => setSelectedDevice(null)}
                onChanged={onDevicesChanged}
            />
        </div>
    );
};

export default DeviceList;
